/**
 * Tracking Plugin for Native Interactive Site Map (ISM)
 *
 * Detects map zoom/pan/map-type interaction, lot status pill selection, mobile pill horizontal
 * scroll, and lot selection on the map, and emits each as a raw payload on the matching
 * module-level Subject in redux/observables.js. Has no analytics knowledge of its own — never
 * imports segmentTrack/googleAnalytics4Event/getAnalyticsTrackingProperties and never dispatches
 * an analytics action. Whichever page embeds this feature owns the actual dispatch — its own
 * tracking files subscribe to those Subjects — imported via this feature's index.js, along with
 * the buildEvent functions in services/segmentEventBuilder.js and
 * services/googleAnalytics4EventBuilder.js — to turn each payload into an actual dispatch. See
 * redux/observables.js and TRACKING_PLUGIN_SEQUENCE.md for the full flow.
 *
 * Pure orchestrator otherwise — wires handlers/mapInteractionHandlers.js,
 * handlers/scrollHandlers.js (bound twice: chip-pill bar and card grid),
 * handlers/getNotifiedInteractionHandlers.js, handlers/delegatedClickHandlers.js (bound three
 * times: Similar Homes toggle, similar-home card, and mobile Sold "See All"), plus Redux store
 * subscriptions. Never touches CSS; the only DOM it reads is the map element, the chips wrapper,
 * the listing content, the panel, and the shared drawer element, each handed to its matching
 * handler above.
 *
 * @module nativeInteractiveSiteMap/plugins/tracking.plugin
 */
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, pairwise, take, throttleTime } from 'rxjs/operators';
import createMapInteractionHandlers from '../handlers/mapInteractionHandlers';
import createScrollHandlers from '../handlers/scrollHandlers';
import createDrawerDragHandlers from '../handlers/drawerDragHandlers';
import createDrawerOpenHandlers from '../handlers/drawerOpenHandlers';
import createGetNotifiedInteractionHandlers from '../handlers/getNotifiedInteractionHandlers';
import createDelegatedClickHandlers from '../handlers/delegatedClickHandlers';
import {
  selectActiveChip,
  selectChipLotCounts,
  selectSelectedLotId,
  selectSelectedTab,
  selectHousePlanCount,
  selectHousingUnitCount,
  selectLots,
  selectFilteredHousePlans,
} from '../redux/selectors';
import {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
} from '../redux/observables';
import { TAB_QUICK_MOVE_INS } from '../constants';

const ZOOM_DEBOUNCE_MS = 300;

// A single drag-to-open gesture trips both the drag handler (pointer movement) and the open
// observer (collapsed→open); throttling the merged stream collapses that into one emission, while
// a plain tap-to-open (open observer only) still emits once.
const DRAWER_INTERACTION_THROTTLE_MS = 500;

/**
 * Number of cards rendered in the panel's active tab (the `card_count` tracking property). Only
 * the Quick Move-Ins tab shows housing-unit cards, and it exists only for the Available chip
 * (services/listViewContentService.js is the source of truth for which chips have tabs) — every
 * other view renders the floor-plan grid — so keying off the selected tab alone is sufficient
 * without re-deriving hasTabs here.
 *
 * @param {object} state
 * @returns {number}
 */
function getActivePanelCardCount(state) {
  return selectSelectedTab(state) === TAB_QUICK_MOVE_INS
    ? selectHousingUnitCount(state)
    : selectHousePlanCount(state);
}

export default function createTrackingPlugin() {
  const state = {
    mapInteractionHandlers: createMapInteractionHandlers(),
    chipScrollHandlers: createScrollHandlers(),
    cardsScrollHandlers: createScrollHandlers(),
    drawerDragHandlers: createDrawerDragHandlers(),
    drawerOpenHandlers: createDrawerOpenHandlers(),
    getNotifiedInteractionHandlers: createGetNotifiedInteractionHandlers(),
    similarHomesToggleHandlers: createDelegatedClickHandlers('[data-similar-homes-toggle]'),
    similarHomeCardHandlers: createDelegatedClickHandlers('[data-similar-home-card]'),
    seeAllHandlers: createDelegatedClickHandlers('[data-sold-see-all-button]'),
    zoomInteraction$: new Subject(),
    drawerInteraction$: new Subject(),
    subscriptions: [],
  };

  return {
    name: 'tracking',

    async init(context) {
      context.state.tracking = { started: false, ready: false };
    },

    async start(context) {
      if (context.state.tracking?.started) {
        return;
      }

      const { getState, store$, mapFacade, mapLibraryLoaded$ } = context.services;
      if (!mapFacade) {
        return;
      }

      context.state.tracking = { started: true, ready: false };

      // Chip pill click -> panelUpdatedOccurred$. distinctUntilChanged already satisfies
      // "if the same pill is clicked again, do not fire"; pairwise() never emits on the first
      // (pre-click, default) chip value, matching lotInteraction.plugin.js's own reasoning for
      // the same pairwise() use — see the plan's Global Constraints for the one known gap
      // (auto-reselect fallback in mapChips.plugin.js also flows through this same subscription).
      function onChipSelectionChanged([previous, next]) {
        panelUpdatedOccurred$.next({
          selectedChip: next.chip,
          previousChip: previous.chip,
          resultsCount: next.counts[next.chip] ?? 0,
          cardCount: getActivePanelCardCount(getState()),
          source: 'lot_status_type',
          isMobile: context.state.isMobile,
        });
      }

      // Lot selected on the map -> lotSelectedOccurred$.
      function onLotSelected(selectedSitePlanLotId) {
        const currentState = getState();
        const lot = selectLots(currentState).find((candidate) => candidate.sitePlanLotId === selectedSitePlanLotId);
        if (!lot) {
          return;
        }

        const matchingHousePlans = selectFilteredHousePlans(currentState)
          .filter((plan) => plan.lotIds.includes(lot.lotId));

        lotSelectedOccurred$.next({
          lot,
          planId: matchingHousePlans.length === 1 ? matchingHousePlans[0].planId : null,
          specId: lot.housingUnitId ?? null,
          plansCount: lot.housePlanIds.length,
          isMobile: context.state.isMobile,
        });

        // A lot selection also updates the panel, so it is a panel update too
        // (source: 'lot_selection') — reported in addition to the lot_selected event above.
        // results_count and card_count describe the current panel state — the active status type's
        // lot count (the pill number, e.g. "Available (26)") and the active tab's cards — derived
        // exactly as they are for the pill path: per the spec, "results count is for the
        // lot_status_type_selected" regardless of what triggered the update. selectedChip carries
        // the selected lot's own status (there is no pill "previousChip" on this path).
        panelUpdatedOccurred$.next({
          selectedChip: lot.status,
          previousChip: null,
          resultsCount: selectChipLotCounts(currentState)[selectActiveChip(currentState)] ?? 0,
          cardCount: getActivePanelCardCount(currentState),
          source: 'lot_selection',
          isMobile: context.state.isMobile,
        });
      }

      // Available chip's selected tab changed -> tabSelectedOccurred$. resultsCount reflects the
      // newly selected tab (getActivePanelCardCount reads selectSelectedTab off currentState,
      // which by the time this fires already reflects the new tab).
      function onTabChanged([previousTab, selectedTab]) {
        const currentState = getState();
        tabSelectedOccurred$.next({
          selectedTab,
          previousTab,
          resultsCount: getActivePanelCardCount(currentState),
          isMobile: context.state.isMobile,
        });
      }

      // Zoom -> mapInteractionOccurred$, debounced so one continuous scroll-wheel/pinch gesture
      // (many rapid zoom_changed ticks) collapses into a single emission once the user pauses,
      // instead of one per tick.
      function onDebouncedZoomInteraction(payload) {
        mapInteractionOccurred$.next(payload);
      }

      function onZoomInteraction(payload) {
        state.zoomInteraction$.next(payload);
      }

      function onPanInteraction({ inputMethod }) {
        mapInteractionOccurred$.next({ interactionType: 'pan', inputMethod });
      }

      function onMapTypeInteraction({ mapType }) {
        mapInteractionOccurred$.next({ interactionType: 'map_type_changed', inputMethod: 'button', mapType });
      }

      function onChipsScrolled() {
        panelScrollOccurred$.next();
      }

      function onCardsScrolled() {
        cardsScrolledOccurred$.next({ isMobile: context.state.isMobile });
      }

      function onNotifyWhenAvailableClicked() {
        notifyWhenAvailableClickedOccurred$.next();
      }

      function onSimilarHomesToggled(toggleButton) {
        const section = toggleButton.closest('[data-similar-homes-section]');
        if (!section) {
          return;
        }

        // Read AFTER the toggle: plugins/listView.plugin.js's own toggle listener is bound earlier in
        // the plugin start sequence (tracking.plugin runs near the end of index.js's plugins array —
        // see TRACKING_PLUGIN_SEQUENCE.md), so by the time this fires aria-expanded already reflects
        // the new state.
        const expanded = toggleButton.getAttribute('aria-expanded') === 'true';

        // Resolved from the DOM, not getState()/selectSelectedLotId: the Sold listing renders
        // every sold home's card (each with its own Similar Homes section) regardless of what's
        // selected on the map, and this toggle can belong to any one of them — not necessarily
        // whichever lot happens to be selected [data-lot-id] is the sold-card-
        // group wrapper rendered in services/listViewContentService.js#renderSoldHomeCards.
        const soldCardGroup = toggleButton.closest('[data-lot-id]');
        const lotId = soldCardGroup ? Number(soldCardGroup.dataset.lotId) : null;
        if (!lotId || Number.isNaN(lotId)) {
          return;
        }

        similarHomesToggledOccurred$.next({
          expanded,
          lotId,
          similarHomesCount: section.querySelectorAll('[data-similar-home-card]').length,
        });
      }

      function onSimilarHomeCardClicked(cardElement) {
        // Resolved from the DOM for the same reason as onSimilarHomesToggled above — this must be
        // the lot whose Sold Home Card contains the clicked similar-home card, not whichever lot
        // is currently selected on the map.
        const soldCardGroup = cardElement.closest('[data-lot-id]');
        const lotId = soldCardGroup ? Number(soldCardGroup.dataset.lotId) : null;
        if (!lotId || Number.isNaN(lotId)) {
          return;
        }

        const list = cardElement.closest('[data-similar-homes-list]');
        const position = list ? Array.from(list.children).indexOf(cardElement) + 1 : null;

        similarHomeCardClickedOccurred$.next({
          lotId,
          planId: cardElement.dataset.planId ? Number(cardElement.dataset.planId) : null,
          specId: cardElement.dataset.housingUnitId ? Number(cardElement.dataset.housingUnitId) : null,
          position,
        });
      }

      function onSeeAllClicked() {
        soldSeeAllClickedOccurred$.next();
      }

      // A vertical drag and the drawer opening are reported as the same interaction — both feed the
      // throttled drawerInteraction$ so a drag-to-open doesn't double-count.
      function onDrawerInteraction() {
        state.drawerInteraction$.next();
      }

      function onMapLibraryLoaded() {
        const mapElement = context.root.querySelector('[data-map-element]');
        const chipsElement = context.root.querySelector('.nhs-c-native-interactive-site-map__chips-wrapper');
        // The panel is mounted into the shared layout drawer — a global node, not inside
        // context.root (drawerService.mountElement moves the panel out of it).
        const drawerElement = document.querySelector('[data-drawer]');

        if (mapElement) {
          state.mapInteractionHandlers.bind(mapFacade, mapElement, {
            onZoomInteraction,
            onPanInteraction,
            onMapTypeInteraction,
          });
        }

        // Chip scroll and drawer drag tracking are both mobile-only.
        if (chipsElement && context.state.isMobile) {
          state.chipScrollHandlers.bind(chipsElement, onChipsScrolled);
        }

        const listingContentElement = context.root.querySelector('.nhs-c-native-interactive-site-map__listing-content');

        // Unlike the chip-pill scroll (mobile-only), the card grid can scroll on any breakpoint.
        if (listingContentElement) {
          state.cardsScrollHandlers.bind(listingContentElement, onCardsScrolled);
        }

        const panelElement = context.root.querySelector('[data-panel]');

        if (panelElement) {
          state.getNotifiedInteractionHandlers.bind(panelElement, onNotifyWhenAvailableClicked);
          state.similarHomesToggleHandlers.bind(panelElement, onSimilarHomesToggled);
          state.similarHomeCardHandlers.bind(panelElement, onSimilarHomeCardClicked);
        }

        if (panelElement && context.state.isMobile) {
          state.seeAllHandlers.bind(panelElement, onSeeAllClicked);
        }

        if (drawerElement && context.state.isMobile) {
          state.drawerDragHandlers.bind(drawerElement, onDrawerInteraction);
          state.drawerOpenHandlers.bind(drawerElement, onDrawerInteraction);
        }

        context.state.tracking.ready = true;
      }

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => ({ chip: selectActiveChip(storeState), counts: selectChipLotCounts(storeState) })),
          distinctUntilChanged((previous, next) => previous.chip === next.chip),
          pairwise(),
        ).subscribe(onChipSelectionChanged)
      );

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => selectSelectedLotId(storeState)),
          distinctUntilChanged(),
          filter((selectedSitePlanLotId) => Boolean(selectedSitePlanLotId)),
        ).subscribe(onLotSelected)
      );

      state.subscriptions.push(
        store$.pipe(
          map((storeState) => selectSelectedTab(storeState)),
          distinctUntilChanged(),
          pairwise(),
          filter(([, next]) => next != null),
        ).subscribe(onTabChanged)
      );

      state.subscriptions.push(
        state.zoomInteraction$.pipe(debounceTime(ZOOM_DEBOUNCE_MS)).subscribe(onDebouncedZoomInteraction)
      );

      state.subscriptions.push(
        state.drawerInteraction$
          .pipe(throttleTime(DRAWER_INTERACTION_THROTTLE_MS))
          .subscribe(() => drawerDraggedOccurred$.next())
      );

      state.subscriptions.push(
        mapLibraryLoaded$.pipe(take(1)).subscribe(onMapLibraryLoaded)
      );
    },

    async stop(context) {
      state.mapInteractionHandlers.unbind();
      state.chipScrollHandlers.unbind();
      state.cardsScrollHandlers.unbind();
      state.getNotifiedInteractionHandlers.unbind();
      state.similarHomesToggleHandlers.unbind();
      state.similarHomeCardHandlers.unbind();
      state.seeAllHandlers.unbind();
      state.drawerDragHandlers.unbind();
      state.drawerOpenHandlers.unbind();
      state.subscriptions.forEach((subscription) => subscription?.unsubscribe?.());
      state.subscriptions = [];
      context.state.tracking = { started: false, ready: false };
    },
  };
}
